<template>
  <div class="coupon-bind-page">
    <h2>绑定礼券</h2>
    <p class="lead">
      输入他人分享的礼券号。绑定成功后，将根据券类型发放相应礼遇。每位用户终身仅可绑定一张礼券。
    </p>
    <p v-if="bonusHint" class="bonus-hint">{{ bonusHint }}</p>

    <el-skeleton v-if="loading && isLoggedIn" rows="3" animated />

    <template v-else>
    <el-alert
      v-if="!isLoggedIn"
      type="info"
      :closable="false"
      show-icon
      title="请先登录"
      description="绑定礼券需要登录账号。注册成功后将引导您到此页填写礼券号。"
      class="block-alert"
    >
      <el-button type="primary" size="small" @click="goLogin">去登录</el-button>
    </el-alert>

    <el-card v-else-if="bindStatus?.hasBinding" shadow="never" class="status-card">
      <h3>已绑定礼券</h3>
      <el-descriptions :column="1" border size="small">
        <el-descriptions-item label="券号">{{ bindStatus.couponNo }}</el-descriptions-item>
        <el-descriptions-item label="类型">
          {{ couponTypeLabel(bindStatus.couponType) }}
        </el-descriptions-item>
        <el-descriptions-item v-if="bindStatus.referrerNickname" label="推荐人">
          {{ bindStatus.referrerNickname }}
        </el-descriptions-item>
      </el-descriptions>
      <div class="actions">
        <el-button @click="$router.push('/referral/earnings')">查看推荐收益</el-button>
        <el-button @click="$router.push('/referral/coupons')">我的礼券</el-button>
      </div>
    </el-card>

    <el-card v-else-if="bindStatus && !bindStatus.canBind" shadow="never" class="bind-card">
      <el-alert type="warning" :closable="false" show-icon title="暂不可绑定">
        <template v-if="bindStatus.daysRemaining === 0 && bindStatus.bindDeadlineAt">
          注册后 {{ bindStatus.bindWithinDaysAfterReg }} 天内可补绑，您的绑定窗口已过期。
        </template>
        <template v-else>
          当前无法绑定礼券，请稍后再试或联系客服。
        </template>
      </el-alert>
    </el-card>

    <el-card v-else shadow="never" class="bind-card">
      <h3>输入券号</h3>
      <p class="hint">
        注册后 {{ bindDays }} 天内可补绑，终身仅可绑定一张礼券。
        <span v-if="daysRemaining != null && daysRemaining >= 0">剩余 {{ daysRemaining }} 天。</span>
      </p>
      <div class="bind-row">
        <el-input
          v-model="couponNo"
          placeholder="例如 RC1A2B3C4D5E"
          maxlength="32"
          clearable
          @blur="onCheck"
          @keyup.enter="submit"
        />
        <el-button type="primary" :loading="binding" @click="submit">绑定</el-button>
      </div>
      <p v-if="checkHint" class="check-hint" :class="{ error: checkError }">{{ checkHint }}</p>
    </el-card>
    </template>
  </div>
</template>

<script>
import { goToLogin, isLoggedIn } from '@/utils/loginFlow'
import {
  bindReferralCoupon,
  checkReferralCoupon,
  fetchReferralBindStatus,
  fetchReferralSettings,
  COUPON_TYPE_LABELS,
  couponTypeLabel
} from '@/utils/referralCoupon'

export default {
  name: 'ReferralCouponBindView',
  data() {
    return {
      couponNo: '',
      checkHint: '',
      checkError: false,
      binding: false,
      bindStatus: null,
      bindDays: 7,
      loading: true,
      bonusHint: ''
    }
  },
  computed: {
    isLoggedIn() {
      return isLoggedIn()
    },
    daysRemaining() {
      const d = this.bindStatus?.daysRemaining
      return d == null ? null : Number(d)
    }
  },
  created() {
    const q = this.$route.query.coupon || this.$route.query.couponNo
    if (typeof q === 'string' && q.trim()) {
      this.couponNo = q.trim()
    }
    this.initPage()
  },
  methods: {
    couponTypeLabel,
    goLogin() {
      goToLogin(this.$router, this.$route.fullPath)
    },
    async initPage() {
      try {
        const settings = await fetchReferralSettings()
        if (settings.bindWithinDaysAfterReg) {
          this.bindDays = settings.bindWithinDaysAfterReg
        }
        const refY = Number(settings.referrerBonusCents || 0) / 100
        const rfeY = Number(settings.refereeBonusCents || 0) / 100
        if (refY > 0 || rfeY > 0) {
          this.bonusHint = `双享礼券绑定成功后，推荐人约 ¥${refY.toFixed(2)}、被绑人约 ¥${rfeY.toFixed(2)} 礼遇（以实际券类型为准）。`
        }
      } catch {
        // 使用默认天数
      }
      if (!this.isLoggedIn) {
        this.loading = false
        if (this.couponNo) {
          await this.onCheck()
        }
        return
      }
      try {
        this.bindStatus = await fetchReferralBindStatus()
        if (!this.bindStatus.hasBinding && this.couponNo) {
          await this.onCheck()
        }
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async onCheck() {
      if (!this.couponNo.trim()) {
        this.checkHint = ''
        this.checkError = false
        return
      }
      try {
        const data = await checkReferralCoupon(this.couponNo)
        if (data.valid) {
          const typeLabel = COUPON_TYPE_LABELS[data.couponType] || ''
          this.checkHint = data.message || `「${data.displayName || ''}」可绑定${typeLabel ? ` · ${typeLabel}` : ''}`
          this.checkError = false
        } else {
          this.checkHint = data.message || '礼券无效'
          this.checkError = true
        }
      } catch {
        this.checkHint = ''
        this.checkError = false
      }
    },
    async submit() {
      if (!this.isLoggedIn) {
        this.goLogin()
        return
      }
      this.binding = true
      try {
        const res = await bindReferralCoupon(this.couponNo)
        this.$message.success(res.message || '绑定成功')
        this.bindStatus = await fetchReferralBindStatus()
        this.couponNo = ''
        this.checkHint = ''
        const href = this.$route.query.href
        if (typeof href === 'string' && href.startsWith('/')) {
          this.$router.push(href)
        }
      } catch (e) {
        this.$message.error(e.message || '绑定失败')
        await this.onCheck()
      } finally {
        this.binding = false
      }
    }
  }
}
</script>

<style scoped>
.coupon-bind-page {
  max-width: 560px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}
h2 {
  margin: 0 0 8px;
}
.lead {
  color: #606266;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 12px;
}
.bonus-hint {
  color: #409eff;
  font-size: 13px;
  margin: 0 0 16px;
}
.block-alert {
  margin-bottom: 20px;
}
.status-card h3,
.bind-card h3 {
  margin: 0 0 12px;
  font-size: 1.05rem;
}
.hint {
  color: #909399;
  font-size: 13px;
  margin: 0 0 16px;
}
.bind-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.bind-row .el-input {
  flex: 1;
  min-width: 200px;
}
.check-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #67c23a;
}
.check-hint.error {
  color: #f56c6c;
}
.actions {
  margin-top: 16px;
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
</style>
